import { Link } from 'react-router-dom';
import SEO from '../../components/seo/SEO';
import SchemaMarkup from '../../components/seo/SchemaMarkup';
import { useState } from 'react';
import { useLanguageLink } from '../../hooks/useLanguageLink';

export default function BlogMLBotDetectionPageRU() {
  const { createLink } = useLanguageLink();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'В чём разница между обнаружением ботов на основе правил и на основе ML?',
      answer: 'Системы на основе правил блокируют трафик по заранее определённым сигнатурам — конкретным IP, user-agent или поведению. Они быстры, но статичны: как только боты меняют сигнатуры, правила их пропускают. ML-системы обучаются на миллионах поведенческих сигналов и непрерывно адаптируются. Они обнаруживают ботов, которых никогда явно не видели, распознавая статистически "неправильное" поведение, даже когда каждый отдельный сигнал выглядит нормально.',
    },
    {
      question: 'Могут ли боты действительно обмануть ML-обнаружение?',
      answer: 'Продвинутые боты действительно пытаются имитировать человеческое поведение: симулируют движение мыши, рандомизируют тайминг кликов, ротируют residential IP и подделывают browser fingerprint. Однако ML-системы анализируют сотни признаков одновременно — энтропию сессии, навигационные паттерны, микровариации тайминга, согласованность устройства — делая полноценную имитацию чрезвычайно сложной. Каждый дополнительный слой обмана вносит собственную статистическую аномалию, которую обученные модели улавливают.',
    },
    {
      question: 'Замедляет ли ML-обнаружение ботов мои landing page?',
      answer: 'Нет. Detection engine Axetra работает на стороне сервера как лёгкий фильтр перед вашим трафиком. Решение разрешить или перенаправить посетителя принимается менее чем за 10 миллисекунд в среднем. Конечные пользователи не ощущают никакой задержки, потому что фильтр отвечает до того, как страница начинает загружаться.',
    },
  ];

  const publishDate = '2026-05-12';

  return (
    <>
      <SEO
        title="Как Machine Learning обнаруживает ботов лучше правил (Гид 2026)"
        description="Узнайте, как ML-фильтрация ботов превосходит системы на основе правил. Разберитесь в поведенческих сигналах, обнаружении аномалий и адаптивных моделях, которые Axetra использует для классификации подозрительного трафика с точностью более 99%."
        canonical="/blog/machine-learning-bot-detection"
        keywords="обнаружение ботов машинным обучением, ML фильтрация трафика, AI обнаружение ботов, обнаружение подозрительного трафика, поведенческий фингерпринтинг, бот против человеческого трафика, обнаружение клик-фрода, автоматизированный трафик"
        ogImage="/images/og-blog-ml-bot-detection.jpg"
        ogType="article"
        publishedTime={publishDate}
        modifiedTime={publishDate}
      />

      <SchemaMarkup
        type="article"
        headline="Как Machine Learning обнаруживает ботов лучше правил (Гид 2026)"
        author="Команда Axetra"
        datePublished={publishDate}
        dateModified={publishDate}
        image="/images/og-blog-ml-bot-detection.jpg"
      />

      <SchemaMarkup type="faq" questions={faqs} />

      <article className="min-h-screen">
        {/* Article Header */}
        <header className="bg-gradient-to-br from-primary-50 via-white to-purple-50 py-16">
          <div className="container-marketing max-w-4xl">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold">
                Технологии
              </span>
              <time className="text-gray-600" dateTime={publishDate}>
                12 мая 2026
              </time>
              <span className="text-gray-400">•</span>
              <span className="text-gray-600">12 мин</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Как Machine Learning обнаруживает ботов лучше, чем системы на основе правил
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
              Традиционные blacklists и статические правила проигрывают гонку вооружений современным ботам. Вот как ML-анализ поведения обнаруживает то, что пропускают правила — и как Axetra использует это для защиты ваших кампаний.
            </p>

            <div className="mt-8 flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                K
              </div>
              <div>
                <div className="font-semibold text-gray-900">Команда Axetra</div>
                <div className="text-gray-600 text-sm">Эксперты по фильтрации трафика</div>
              </div>
            </div>
          </div>
        </header>

        {/* Article Content */}
        <div className="bg-white py-16">
          <div className="container-marketing max-w-4xl prose prose-lg max-w-none">

            {/* Introduction */}
            <section className="mb-12">
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Каждый год рекламодатели теряют миллиарды долларов из-за бот-трафика. Клик-фермы сливают рекламные бюджеты. Парсеры крадут контент. Автоматизированные скрипты искажают аналитику, портят A/B-тесты и приводят к блокировке рекламных аккаунтов Facebook. На протяжении многих лет ответ индустрии был одним и тем же: создать более крупный blacklist.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                Сегодня этот подход больше не работает. Современные боты не используют известные дата-центры или очевидные user-agent строки. Они работают через residential proxy, меняют идентичности и достаточно хорошо имитируют человеческое поведение, чтобы обходить простое сопоставление шаблонов. <strong>Блокировать их правилами — всё равно что пытаться остановить воду сеткой.</strong>
              </p>
              <p className="text-xl text-gray-700 leading-relaxed">
                Machine learning меняет правила игры. Вместо вопроса «совпадает ли этот посетитель с известным плохим шаблоном?» ML задаёт вопрос: «ведёт ли этот посетитель себя как реальный человек?». Этот переход — от сопоставления сигнатур к пониманию поведения — и объясняет, почему ML-обнаружение находит ботов, которых правила обнаружить не могут.
              </p>
            </section>

            {/* Why Rules Fail */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Почему системы на основе правил проигрывают гонку вооружений
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Системы на основе правил работают по простому принципу: определить, как выглядит плохой трафик, а затем блокировать его. Заблокировать этот диапазон IP. Заблокировать тот user-agent. Блокировать запросы без referrer. В начале 2010-х этого было достаточно.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Проблема в том, что боты эволюционировали быстрее, чем поддерживались правила. Сегодняшние продвинутые боты:
              </p>

              <ul className="space-y-3 text-lg text-gray-700 mb-8">
                {[
                  { label: 'Используют residential IP', desc: 'полученные с реальных пользовательских устройств через proxy-сети, что делает IP blacklist бесполезным' },
                  { label: 'Подделывают легитимные browser fingerprint', desc: 'имитируя Chrome на Windows с реальными разрешениями экрана и наборами шрифтов' },
                  { label: 'Симулируют движение мыши и скроллинг', desc: 'используя заранее записанные человеческие паттерны взаимодействия для прохождения базовых поведенческих проверок' },
                  { label: 'Изменяют время кликов и длительность сессий', desc: 'избегая очевидно нечеловеческих скоростных шаблонов, которые ловят простые правила' },
                  { label: 'Используют реальные TLS fingerprint', desc: 'поэтому сетевой анализ не может отличить их от настоящих браузеров' },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <span><strong>{item.label}</strong> — {item.desc}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-amber-50 border-l-4 border-amber-500 p-6 mb-6">
                <p className="text-gray-700">
                  <strong>Ключевая проблема:</strong> Каждый раз, когда публикуется новое правило, операторы ботов обновляют свои скрипты, чтобы его обходить. Списки правил всегда ловят вчерашних ботов. Machine learning обобщает паттерны вместо запоминания конкретных сигнатур — поэтому оно ловит и завтрашних ботов.
                </p>
              </div>
            </section>

            {/* How ML Works */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Как Machine Learning подходит к обнаружению ботов
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                ML-обнаружение ботов обучает модели на размеченных датасетах реальных человеческих сессий и известных бот-сессий. Модель изучает, какие комбинации сигналов статистически связаны с каждым классом. Во время inference — когда приходит новый посетитель — модель оценивает сотни признаков и выдаёт вероятностный score.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Ключевая идея заключается в том, что ни один отдельный сигнал не является окончательным. Бот может подделать любой один признак. Но подделать их все одновременно так, чтобы получить естественное человеческое совместное распределение, экспоненциально сложнее. Именно это и использует ML.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
                {[
                  {
                    title: 'Network & Identity Signals',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ),
                    from: 'from-blue-500',
                    to: 'to-blue-700',
                    desc: 'Проверяет реальное происхождение каждого запроса — репутацию IP, тип сети и географическую согласованность.',
                  },
                  {
                    title: 'Browser & Device Fingerprint',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    ),
                    from: 'from-emerald-500',
                    to: 'to-emerald-700',
                    desc: 'Идентифицирует реальное устройство запроса, обнаруживая инструменты автоматизации и headless-браузеры независимо от подделанных заголовков.',
                  },
                  {
                    title: 'Behavioral Timing Analysis',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    ),
                    from: 'from-violet-500',
                    to: 'to-violet-700',
                    desc: 'Измеряет движение мыши, паттерны скролла и глубину вовлечённости, чтобы отличить живого пользователя от скрипта.',
                  },
                  {
                    title: 'Traffic Pattern Signals',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    ),
                    from: 'from-orange-500',
                    to: 'to-orange-600',
                    desc: 'Отслеживает частоту запросов, цепочки referrer и межсессионную согласованность, выявляя скоординированную бот-активность.',
                  },
                ].map((group) => (
                  <div key={group.title} className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                    <div className={`bg-gradient-to-r ${group.from} ${group.to} px-5 py-4 flex items-center gap-3`}>
                      <div className="text-white opacity-90">{group.icon}</div>
                      <h3 className="font-semibold text-white text-base tracking-tight">{group.title}</h3>
                    </div>
                    <div className="bg-white px-5 py-4">
                      <p className="text-gray-600 text-sm leading-relaxed">{group.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6">
                <p className="text-gray-700 italic">
                  <strong>Почему это важно:</strong> Бот может успешно подделать browser fingerprint. Но если его поведенческий тайминг роботизирован или сетевые сигналы противоречат заявленной локации, модель всё равно классифицирует его правильно. Одного метода обхода недостаточно.
                </p>
              </div>
            </section>

            {/* Anomaly Detection */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Обнаружение аномалий: выявление ботов, которых мы никогда не видели
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Один из самых мощных аспектов ML — это <strong>обнаружение аномалий</strong> — способность помечать трафик как подозрительный, даже если он не совпадает ни с одной известной сигнатурой бота. На практике это чрезвычайно важно, потому что новые техники ботов появляются постоянно.
              </p>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Модели обнаружения аномалий изучают статистическое распределение нормального человеческого трафика. Когда поведенческий профиль посетителя выходит за пределы этого распределения — даже тонкими, трудно формулируемыми способами — модель присваивает высокий anomaly score. Это позволяет обнаруживать zero-day бот-скрипты, новые клик-фермы и специально созданные инструменты автоматизации до того, как кто-либо написал для них правило.
              </p>

              <div className="bg-gray-50 rounded-xl p-8 mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Реальный пример: «Слишком идеальный» бот</h3>
                <p className="text-gray-700 mb-4">
                  Оператор клик-фермы обучает ботов имитировать человеческое поведение: случайные задержки, реалистичные траектории мыши, события скроллинга. Для системы на основе правил эти боты проходят идеально. Для модели обнаружения аномалий — нет.
                </p>
                <p className="text-gray-700">
                  Почему? Потому что тайминг реальных людей <em>неидеально случайный</em> — на него влияют когнитивная нагрузка, отвлечения и скорость чтения. Сгенерированная ботом «случайность» обычно статистически слишком равномерна, слишком равномерно распределена. Модель определяет, что паттерн дисперсии создан машиной, даже если каждое отдельное действие выглядит нормальным.
                </p>
              </div>
            </section>

            {/* Axetra Technology */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Как Axetra использует ML для фильтрации вашего трафика
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Система фильтрации трафика Axetra построена на многоуровневой архитектуре, предназначенной для классификации каждого визита в реальном времени без влияния на скорость загрузки страницы.
              </p>

              <div className="space-y-4 mb-10">
                {[
                  {
                    step: '1',
                    title: 'Высокоскоростной сетевой фильтр',
                    desc: 'Каждый запрос сначала проходит через основной фильтр Axetra, обеспечивающий обработку менее чем за миллисекунду. Этот уровень выполняет проверку репутации IP, классификацию ASN (дата-центр vs. residential), известные диапазоны proxy/VPN и анализ User-Agent. Результаты возвращаются до начала рендеринга вашей страницы.',
                    badge: 'Latency: < 5ms',
                    badgeColor: 'green',
                  },
                  {
                    step: '2',
                    title: 'Device & Browser Fingerprinting',
                    desc: 'Лёгкий клиентский зонд собирает более 40 сигналов браузера и оборудования: вывод canvas rendering, WebGL vendor strings, метрики шрифтов, геометрию экрана и hardware concurrency. Они объединяются в стабильный device fingerprint, который сохраняется при смене IP — поэтому даже боты с ротирующими proxy отслеживаются как одна сущность.',
                    badge: 'Identifies: Headless browsers, automation tools',
                    badgeColor: 'blue',
                  },
                  {
                    step: '3',
                    title: 'Behavioral Scoring Engine',
                    desc: 'Поведенческие сигналы уровня сессии — тайминг взаимодействия, навигационные паттерны и глубина вовлечения — передаются в ML scoring model. Модель сравнивает поведенческий профиль каждой сессии с изученным распределением реальных посетителей в вашей вертикали. Сессии со значительными отклонениями получают повышенные risk score.',
                    badge: 'Accuracy: > 99% on known bot types',
                    badgeColor: 'purple',
                  },
                  {
                    step: '4',
                    title: 'Аналитика событий в реальном времени',
                    desc: 'Все решения классификации и их feature vector логируются в аналитический движок Axetra, обеспечивая агрегацию в реальном времени по миллионам событий в секунду. Это питает ваш campaign dashboard и возвращается в переобучение модели, поэтому обнаружение улучшается по мере накопления данных трафика.',
                    badge: 'Scales: Millions of events/second',
                    badgeColor: 'orange',
                  },
                  {
                    step: '5',
                    title: 'Adaptive Rule Refinement',
                    desc: 'ML-модель непрерывно выявляет новые кластеры ботов, анализируя паттерны в последних false-negative отчётах и возникающих аномалиях трафика. Когда новая техника ботов появляется в нескольких кампаниях, система Axetra автоматически усиливает обнаружение без необходимости ручного обновления правил.',
                    badge: 'Updates: Continuous',
                    badgeColor: 'red',
                  },
                ].map((layer) => (
                  <div key={layer.step} className="flex gap-6 p-6 border border-gray-200 rounded-xl">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-600 text-white rounded-full font-bold text-lg flex-shrink-0">
                      {layer.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-xl font-bold text-gray-900">{layer.title}</h3>
                        <span className={`px-3 py-1 bg-${layer.badgeColor}-100 text-${layer.badgeColor}-700 rounded-full text-xs font-semibold`}>
                          {layer.badge}
                        </span>
                      </div>
                      <p className="text-gray-700">{layer.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-primary-50 border border-primary-200 rounded-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Что это означает для ваших кампаний</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { stat: '< 10ms', label: 'Среднее время классификации', sub: 'Посетители никогда не ждут' },
                    { stat: '120+', label: 'Категорий правил обнаружения', sub: 'Покрытие всех основных типов ботов' },
                    { stat: '99%+', label: 'Точность на известных ботах', sub: 'Практически нулевой false positive rate' },
                  ].map((s) => (
                    <div key={s.stat} className="text-center">
                      <div className="text-4xl font-bold text-primary-600 mb-1">{s.stat}</div>
                      <div className="font-semibold text-gray-900 mb-1">{s.label}</div>
                      <div className="text-sm text-gray-600">{s.sub}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Business Impact */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Бизнес-влияние улучшенного обнаружения ботов
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                Улучшенное обнаружение — это не просто техническая победа. Оно напрямую трансформируется в измеримые бизнес-результаты по каждому каналу с платным трафиком.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {[
                  {
                    icon: (
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                        <path d="M2.273 5.625A4.483 4.483 0 0 1 5.25 4.5h13.5c1.141 0 2.183.425 2.977 1.125A3 3 0 0 0 18.75 3H5.25a3 3 0 0 0-2.977 2.625ZM2.273 8.625A4.483 4.483 0 0 1 5.25 7.5h13.5c1.141 0 2.183.425 2.977 1.125A3 3 0 0 0 18.75 6H5.25a3 3 0 0 0-2.977 2.625ZM5.25 9a3 3 0 0 0-3 3v6a3 3 0 0 0 3 3h13.5a3 3 0 0 0 3-3v-6a3 3 0 0 0-3-3H15a.75.75 0 0 0-.75.75 2.25 2.25 0 0 1-4.5 0A.75.75 0 0 0 9 9H5.25Z" />
                      </svg>
                    ),
                    color: 'text-emerald-600 bg-emerald-50',
                    title: 'Защита рекламного бюджета',
                    desc: 'Каждый клик бота расходует реальный бюджет без шанса на конверсию. ML-фильтрация устраняет эти потери, улучшая ваш эффективный cost-per-acquisition за счёт удаления неконвертирующего бот-трафика.',
                  },
                  {
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                        <path d="M3 13a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6Z" />
                        <path d="M15 9a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V9Z" />
                        <path d="M9 5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1h-4a1 1 0 0 1-1-1V5Z" />
                        <path d="M4 20h14" />
                        <path d="M18 4l2 2-2 2" />
                        <circle cx="20" cy="6" r="0" />
                      </svg>
                    ),
                    color: 'text-blue-600 bg-blue-50',
                    title: 'Чистые аналитические данные',
                    desc: 'Бот-трафик искусственно увеличивает количество сессий, снижает конверсию и искажает attribution model. После фильтрации ботов аналитика отражает реальное поведение пользователей — решения по оптимизации становятся надёжными.',
                  },
                  {
                    icon: (
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                        <path fillRule="evenodd" d="M12.516 2.17a.75.75 0 0 0-1.032 0 11.209 11.209 0 0 1-7.877 3.08.75.75 0 0 0-.722.515A12.74 12.74 0 0 0 2.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 0 0 .374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 0 0-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08Zm3.094 8.016a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                      </svg>
                    ),
                    color: 'text-violet-600 bg-violet-50',
                    title: 'Безопасность рекламных аккаунтов',
                    desc: 'Такие платформы, как Facebook и Google, отслеживают качество трафика на ваших landing page. Высокая доля бот-трафика — одна из главных причин policy flag и блокировок аккаунтов. Фильтрация поддерживает чистоту ваших quality signal.',
                  },
                  {
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                        <path d="M21 17H13L9.5 12H3" />
                        <path d="M21 7H13L9.505 12" />
                        <path d="M18 10l3-3-3-3" />
                        <path d="M18 20l3-3-3-3" />
                      </svg>
                    ),
                    color: 'text-orange-600 bg-orange-50',
                    title: 'Точное A/B-тестирование',
                    desc: 'Бот-трафик распределяется по тестовым вариантам с иным распределением, чем реальные пользователи, внося систематическое смещение. Чистый трафик означает статистически корректные и actionable результаты тестов.',
                  },
                  {
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                        <circle cx="12" cy="12" r="1" />
                        <circle cx="12" cy="12" r="5" />
                        <circle cx="12" cy="12" r="9" />
                        <path d="M15 6V3h3" />
                        <path d="M15 9l-3 3" />
                        <path d="M18 3l-3 3" />
                      </svg>
                    ),
                    color: 'text-rose-600 bg-rose-50',
                    title: 'Улучшенные алгоритмы ставок',
                    desc: 'Автоматические ставки на рекламных платформах обучаются на сигналах конверсий. Когда боты загрязняют этот сигнал, алгоритм оптимизируется под неправильную аудиторию. Уберите ботов — и алгоритм обучается на реальных конверсиях.',
                  },
                  {
                    icon: (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6">
                        <path d="M9 12h6" />
                        <path d="M12 9v6" />
                        <path d="M4 6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v7a9 9 0 0 1-9 9 9 9 0 0 1-9-9V6Z" />
                      </svg>
                    ),
                    color: 'text-slate-600 bg-slate-50',
                    title: 'Защита контента и офферов',
                    desc: 'Конкуренты используют парсеры для мониторинга ваших офферов, копирования landing page и демпинга цен в реальном времени. ML-обнаружение идентифицирует scraper-трафик и блокирует его до извлечения данных.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4 p-6 bg-gray-50 rounded-xl">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color}`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-700 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Comparison table */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Системы на основе правил vs. ML: сравнение
              </h2>

              <div className="overflow-x-auto rounded-xl border border-gray-200 mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left p-4 font-semibold text-gray-900">Возможность</th>
                      <th className="text-center p-4 font-semibold text-gray-500">На основе правил</th>
                      <th className="text-center p-4 font-semibold text-primary-600">ML (Axetra)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {[
                      ['Блокирует известные бот-IP', '✅', '✅'],
                      ['Обнаруживает residential proxy ботов', '❌', '✅'],
                      ['Идентифицирует headless browser автоматизацию', '⚠️ Частично', '✅'],
                      ['Ловит новые / zero-day бот-скрипты', '❌', '✅'],
                      ['Адаптируется к новым техникам обхода', '❌ Только вручную', '✅ Непрерывно'],
                      ['Анализирует поведенческие паттерны тайминга', '❌', '✅'],
                      ['Проверяет согласованность device fingerprint', '❌', '✅'],
                      ['False positive rate на реальных пользователях', 'Средний', 'Очень низкий'],
                      ['Обнаружение улучшается со временем', '❌', '✅ Авто-обучение'],
                      ['Работает без обновления списков правил', '❌', '✅'],
                    ].map(([capability, rules, ml], i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="p-4 text-gray-700 font-medium">{capability}</td>
                        <td className="p-4 text-center text-gray-600">{rules}</td>
                        <td className="p-4 text-center font-medium text-primary-700">{ml}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Getting Started */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Начало работы с ML-фильтрацией трафика
              </h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Переход от фильтрации на основе правил к ML-фильтрации не требует замены существующей инфраструктуры. Axetra интегрируется как лёгкий фильтр перед вашими существующими landing page — без изменений в рекламной платформе, настройках трекинга или бэкенде.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { step: '1', title: 'Создайте кампанию', desc: 'Настройте кампанию в dashboard Axetra. Определите, что происходит с бот-трафиком: тихая блокировка, отклонение запроса или логирование для анализа.' },
                  { step: '2', title: 'Добавьте integration snippet', desc: 'Вставьте одну строку кода в ваш landing page. Это направляет каждого посетителя через classification engine Axetra до рендеринга страницы.' },
                  { step: '3', title: 'Анализируйте качество трафика', desc: 'Dashboard Axetra показывает в реальном времени breakdown bot vs. human трафика, категории обнаружения, географическое распределение и тренды — всё с мгновенной агрегацией.' },
                  { step: '4', title: 'Позвольте модели улучшаться', desc: 'По мере работы кампаний поведенческая модель Axetra строит профиль реальных посетителей для ваших конкретных офферов и вертикалей, делая обнаружение всё точнее со временем.' },
                ].map((s) => (
                  <div key={s.step} className="flex gap-4">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary-600 text-white rounded-full font-bold flex-shrink-0">
                      {s.step}
                    </div>
                    <div className="pt-1">
                      <strong className="text-gray-900">{s.title}</strong>
                      <p className="text-gray-700 mt-1">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-3">Готовы увидеть ML-обнаружение ботов в действии?</h3>
                <p className="text-primary-100 mb-6 text-lg">
                  Присоединяйтесь к тысячам рекламодателей, которые перестали терять бюджет на ботов. Начните фильтровать трафик умнее уже сегодня.
                </p>
                <Link
                  to={createLink('register')}
                  className="inline-block bg-white text-primary-600 font-bold px-8 py-4 rounded-xl hover:bg-primary-50 transition-colors text-lg"
                >
                  Начать бесплатный пробный период
                </Link>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">Часто задаваемые вопросы</h2>

              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                      <svg
                        className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-6">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* Conclusion */}
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Заключение</h2>

              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Проблема ботов никуда не исчезнет. По мере улучшения систем обнаружения операторы ботов всё больше инвестируют в методы обхода. Единственная устойчивая защита — это система, которая обучается непрерывно, которой не нужны люди для написания новых правил каждый раз, когда появляется новая техника ботов.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                ML-обнаружение ботов — это уже не роскошь для enterprise-команд. Это базовое требование для любого серьёзного рекламодателя, работающего с платным трафиком в 2026 году. Axetra делает эту технологию доступной для кампаний любого размера без инфраструктурных накладных расходов.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Рекламодатели, которые защищают качество своего трафика уже сейчас, получат более чистые данные, лучше обученные алгоритмы и более здоровые рекламные аккаунты по сравнению с конкурентами, которые всё ещё борются со вчерашними ботами вчерашними правилами.
              </p>
            </section>

          </div>
        </div>
      </article>
    </>
  );
}
